// pages/quotations/QuotationList.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import { userActions } from '../../core/action/userAction';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import dayjs from 'dayjs';
import Select from 'react-select';
import { DatePicker } from 'antd';
import Table from '../../core/pagination/datatable';
import {
    ChevronUp,
    RotateCcw,
    Sliders,
    StopCircle,
    User,
    Calendar,
    Filter,
    Search,
    Eye,
    Edit,
    Trash2,
    PlusCircle,
    FileText,
    CheckCircle,
    XCircle,
    AlertTriangle,
    Clock,
    RefreshCw,
    File
} from 'feather-icons-react/build/IconComponents';
import { quotationActions } from '../../core/action/quotationActions';
import { customerActions } from '../../core/action/customerActions';
import { companyActions } from '../../core/action/companyActions';
import { bankActions } from '../../core/action/bankActions';
import ViewQuotation from '../../core/modals/quotations/ViewQuotation';
import EditQuotation from '../../core/modals/quotations/EditQuotation';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const { RangePicker } = DatePicker;
const MySwal = withReactContent(Swal);

const QuotationList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    // Permission checks
    const canCreate = userActions.hasPermission('create-quotations');
    const canEdit = userActions.hasPermission('edit-quotations');
    const canDelete = userActions.hasPermission('delete-quotations');

    // State
    const [activeTab, setActiveTab] = useState('quotation');
    const [quotations, setQuotations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isFilterVisible, setIsFilterVisible] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [companySettings, setCompanySettings] = useState({});

    const [customers, setCustomers] = useState([]);
    const [modalType, setModalType] = useState(null);
    const [selectedQuotation, setSelectedQuotation] = useState(null);

    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });

    const [filters, setFilters] = useState({
        search: '',
        startDate: null,
        endDate: null,
        customer_id: 0,
        status: ''
    });

    const sortOptions = [
        { value: 'newest', label: 'Newest First' },
        { value: 'oldest', label: 'Oldest First' },
        { value: 'quotation_asc', label: 'Quotation No (A-Z)' },
        { value: 'quotation_desc', label: 'Quotation No (Z-A)' },
        { value: 'amount_high', label: 'Highest Amount' },
        { value: 'amount_low', label: 'Lowest Amount' },
        { value: 'customer_asc', label: 'Customer Name (A-Z)' },
        { value: 'customer_desc', label: 'Customer Name (Z-A)' }
    ];
    const [sortOption, setSortOption] = useState('newest');

    const statusFilterOptions = [
        { value: '', label: 'All Status' },
        { value: 'draft', label: 'Draft' },
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'expired', label: 'Expired' },
        { value: 'converted', label: 'Converted' }
    ];

    const datePresets = {
        'Today': [dayjs(), dayjs()],
        'Yesterday': [dayjs().subtract(1, 'day'), dayjs().subtract(1, 'day')],
        'Last 7 Days': [dayjs().subtract(7, 'day'), dayjs()],
        'Last 30 Days': [dayjs().subtract(30, 'day'), dayjs()],
        'This Month': [dayjs().startOf('month'), dayjs().endOf('month')],
        'Last Month': [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')]
    };

    useEffect(() => {
        loadCompanySettings();
        loadCustomers();
        loadQuotations(1);
    }, [activeTab]);

    const loadCompanySettings = async () => {
        try {
            const result = await companyActions.getSettings();
            if (result.success) setCompanySettings(result.data || {});
        } catch (error) { console.error(error); }
    };

    const loadCustomers = async () => {
        try {
            const result = await customerActions.getCustomers({ limit: 1000 });
            if (result.success) {
                setCustomers([
                    { value: 0, label: 'All Customers' },
                    ...result.data.map(c => ({ value: c.id, label: `${c.name} (${c.phone || 'No phone'})` }))
                ]);
            }
        } catch (error) { console.error(error); }
    };

    const loadQuotations = async (page = pagination.page) => {
        setLoading(true);
        try {
            const params = {
                page,
                limit: pagination.limit,
                search: filters.search,
                start_date: filters.startDate ? filters.startDate.format('YYYY-MM-DD') : undefined,
                end_date: filters.endDate ? filters.endDate.format('YYYY-MM-DD') : undefined,
                customer_id: filters.customer_id || undefined,
                status: filters.status || undefined,
                sort: sortOption,
                doc_type: activeTab // 'quotation' or 'proforma'
            };
            const result = await quotationActions.getQuotations(params);
            if (result.success) {
                setQuotations(result.data || []);
                setPagination({
                    page: result.page || page,
                    limit: pagination.limit,
                    total: result.total || 0,
                    totalPages: result.totalPages || 1
                });
            } else {
                MySwal.fire({ icon: 'error', title: 'Error', text: result.message || 'Failed to load quotations', timer: 2000 });
            }
        } catch (error) {
            MySwal.fire({ icon: 'error', title: 'Error', text: 'Failed to load quotations', timer: 2000 });
        } finally { setLoading(false); }
    };

    const handleSearch = () => { setPagination(prev => ({ ...prev, page: 1 })); loadQuotations(1); };
    const toggleFilterVisibility = () => setIsFilterVisible(prev => !prev);

    const resetFilters = () => {
        setFilters({ search: '', startDate: null, endDate: null, customer_id: 0, status: '' });
        setSortOption('newest');
        setPagination(prev => ({ ...prev, page: 1 }));
        loadQuotations(1);
    };

    const handleDateRangeChange = (dates) => {
        if (dates && dates.length === 2) setFilters(prev => ({ ...prev, startDate: dates[0], endDate: dates[1] }));
        else setFilters(prev => ({ ...prev, startDate: null, endDate: null }));
    };

    const handleFilterChange = (filterName, value) => setFilters(prev => ({ ...prev, [filterName]: value }));
    const handleSortChange = (option) => { setSortOption(option?.value || 'newest'); setPagination(prev => ({ ...prev, page: 1 })); setTimeout(() => loadQuotations(1), 0); };
    const handlePageChange = (page) => { setPagination(prev => ({ ...prev, page })); loadQuotations(page); };

    const handleView = (quotation) => { setSelectedQuotation(quotation); setModalType('view'); };
    const handleEdit = (quotation) => {
        if (!canEdit) { MySwal.fire({ title: 'Access Denied', text: 'No permission to edit', icon: 'error' }); return; }
        setSelectedQuotation(quotation);
        setModalType('edit');
    };
    const handleModalClose = () => { setModalType(null); setSelectedQuotation(null); };
    const handleSuccess = () => { loadQuotations(pagination.page); handleModalClose(); };

    const handleDelete = async (quotation) => {
        if (!canDelete) { MySwal.fire({ title: 'Access Denied', text: 'No permission to delete', icon: 'error' }); return; }
        const confirm = await MySwal.fire({
            title: 'Delete Quotation?',
            html: `<p>Delete <strong>${quotation.quotation_no}</strong> for ${quotation.customer_name}?<br>This action cannot be undone.</p>`,
            icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Yes, delete'
        });
        if (confirm.isConfirmed) {
            try {
                const result = await quotationActions.deleteQuotation(quotation.id);
                if (result.success) {
                    MySwal.fire({ icon: 'success', title: 'Deleted!', timer: 2000 });
                    loadQuotations(pagination.page);
                } else MySwal.fire({ icon: 'error', title: 'Error', text: result.message });
            } catch (error) { MySwal.fire({ icon: 'error', title: 'Error', text: error.message }); }
        }
    };

    // eslint-disable-next-line no-unused-vars
    const handleConvertToSale = async (id) => {
        const confirm = await MySwal.fire({ title: 'Convert to Sale?', text: 'Create sales invoice from this quotation.', icon: 'question', showCancelButton: true, confirmButtonColor: '#28a745', confirmButtonText: 'Yes, convert' });
        if (confirm.isConfirmed) {
            try {
                const result = await quotationActions.convertToSale(id);
                if (result.success) {
                    MySwal.fire({ icon: 'success', title: 'Converted!', text: 'Quotation converted to sale', timer: 2000 });
                    loadQuotations(pagination.page);
                } else MySwal.fire({ icon: 'error', title: 'Error', text: result.message });
            } catch (error) { MySwal.fire({ icon: 'error', title: 'Error', text: error.message }); }
        }
    };

    // PDF generation — GST Tax Invoice format (same as Sales PDF)
    const downloadQuotationPDF = async (quotation, copyType = 'original') => {
        const docType = activeTab; // 'quotation' or 'proforma'
        try {
            setLoading(true);
            MySwal.fire({ title: 'Generating PDF...', text: 'Please wait', allowOutsideClick: false, didOpen: () => MySwal.showLoading() });

            const [qResult, bankResult] = await Promise.all([
                quotationActions.getQuotation(quotation.id),
                bankActions.getAccounts({ status: 'active' })
            ]);
            if (!qResult.success || !qResult.data) throw new Error(qResult?.message || 'Failed to load details');
            const responseData = qResult.data;
            const full = responseData.quotation || responseData;
            const items = responseData.items || full.items || [];
            const bankAccounts = bankResult.success ? (bankResult.data || []) : [];
            const defaultBank = bankAccounts.find(b => b.is_default === 1) || bankAccounts[0] || null;

            // ── Number-to-words (Indian system) ────────────────────────────
            const toWords = (num) => {
                const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
                    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
                const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
                const convert = (n) => {
                    if (n < 20) return ones[n];
                    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
                    if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + convert(n % 100) : '');
                    if (n < 100000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
                    if (n < 10000000) return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
                    return convert(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
                };
                const rupees = Math.floor(num);
                const paise = Math.round((num - rupees) * 100);
                let result = 'INR ' + (rupees === 0 ? 'Zero' : convert(rupees));
                if (paise > 0) result += ' and ' + convert(paise) + ' Paise';
                return result + ' Only';
            };

            // ── Page setup ──────────────────────────────────────────────────
            const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
            const PW = doc.internal.pageSize.width;
            const M = 7;
            const CW = PW - M * 2;

            // ── Document title & copy label ─────────────────────────────────
            const docTitle = docType === 'proforma' ? 'Proforma Invoice' : 'Quotation';
            const copyTypeMap = { original: 'ORIGINAL FOR RECIPIENT', duplicate: 'DUPLICATE FOR SUPPLIER', extra: 'EXTRA COPY' };
            const copyLabel = copyTypeMap[copyType] || 'ORIGINAL FOR RECIPIENT';

            // ── Company (seller) info ────────────────────────────────────────
            const compName = companySettings?.company_name || 'Your Company';
            const compAddress = companySettings?.company_address || '';
            const compGST = companySettings?.gst_number || '';
            const compState = companySettings?.company_state || '';
            const compStateCode = companySettings?.state_code || '';
            const compEmail = companySettings?.company_email || '';
            const compPhone = companySettings?.company_phone || '';

            // ── Document fields ──────────────────────────────────────────────
            const docNo = full.quotation_no || quotation.quotation_no || 'N/A';
            const docDate = dayjs(full.quotation_date || quotation.quotation_date).format('DD-MMM-YY');
            const expiryDate = full.expiry_date ? dayjs(full.expiry_date).format('DD-MMM-YY') : '';
            const referenceNo = full.reference_no || '';
            const notes = full.notes || '';
            const termsConditions = full.terms_conditions || '';
            const status = full.status || '';

            // ── Customer fields ──────────────────────────────────────────────
            const custName = full.customer_name || quotation.customer_name || 'N/A';
            const custAddress = full.customer_address || '';
            const custGST = full.customer_gst || '';
            const custState = full.customer_state || '';
            const custStateCode = full.customer_state_code || '';

            // ── Financials ───────────────────────────────────────────────────
            const subtotal = parseFloat(full.subtotal || 0);
            const tax = parseFloat(full.tax || 0);
            const itemsTotalTax = items.reduce((s, i) => s + parseFloat(i.tax_amount || 0), 0);
            const cgst = parseFloat(full.cgst) || (itemsTotalTax / 2) || (tax / 2);
            const sgst = parseFloat(full.sgst) || (itemsTotalTax / 2) || (tax / 2);
            const cgstRate = parseFloat(full.cgst_rate || 9);
            const sgstRate = parseFloat(full.sgst_rate || 9);
            const discount = parseFloat(full.discount || 0);
            const roundOff = parseFloat(full.round_off || 0);
            const grandTotal = parseFloat(full.grand_total || quotation.grand_total || 0);

            // ── Helpers ──────────────────────────────────────────────────────
            const fmt3 = (n) => parseFloat(n || 0).toFixed(3);
            const fmt2 = (n) => parseFloat(n || 0).toFixed(2);
            const BK = [0, 0, 0];
            const setBlack = () => { doc.setTextColor(...BK); doc.setDrawColor(...BK); };
            const hLine = (x1, y, x2) => { doc.setLineWidth(0.3); doc.setDrawColor(...BK); doc.line(x1, y, x2, y); };
            const vLine = (x, y1, y2) => { doc.setLineWidth(0.3); doc.setDrawColor(...BK); doc.line(x, y1, x, y2); };
            const drawBox = (x, y, w, h) => { doc.setLineWidth(0.3); doc.setDrawColor(...BK); doc.rect(x, y, w, h, 'S'); };

            let y = M;

            // ── PAGE TITLE ───────────────────────────────────────────────────
            doc.setFontSize(13); doc.setFont('helvetica', 'bold'); setBlack();
            doc.text(docTitle, PW / 2, y + 6, { align: 'center' });
            doc.setFontSize(8); doc.setFont('helvetica', 'normal');
            doc.text(`(${copyLabel})`, M + CW, y + 6, { align: 'right' });
            y += 10;

            // ── SECTION 1: COMPANY INFO (left) + DOCUMENT GRID (right) ───────
            const leftW = Math.round(CW * 0.52);
            const rightW = CW - leftW;
            const leftX = M;
            const rightX = M + leftW;
            const numFieldRows = 5;
            const fieldH = 9;
            const headerH = numFieldRows * fieldH;

            drawBox(M, y, CW, headerH);
            vLine(rightX, y, y + headerH);

            let cy = y + 4;
            doc.setFontSize(10); doc.setFont('helvetica', 'bold'); setBlack();
            doc.text(compName, leftX + 2, cy); cy += 5;
            doc.setFontSize(7.5); doc.setFont('helvetica', 'normal');
            const addrLines = doc.splitTextToSize(compAddress, leftW - 4);
            doc.text(addrLines, leftX + 2, cy); cy += addrLines.length * 3.8 + 1;
            if (compGST) { doc.text(`GSTIN/UIN: ${compGST}`, leftX + 2, cy); cy += 4; }
            if (compState) { doc.text(`State Name : ${compState}${compStateCode ? ', Code : ' + compStateCode : ''}`, leftX + 2, cy); cy += 4; }
            if (compEmail) { doc.text(`E-Mail : ${compEmail}`, leftX + 2, cy); cy += 4; }
            if (compPhone) { doc.text(`Ph : ${compPhone}`, leftX + 2, cy); }

            // ── Document fields grid (right) ─────────────────────────────────
            const fieldMidX = rightX + rightW / 2;
            const fieldRows = [
                [docType === 'proforma' ? 'Proforma No.' : 'Quotation No.', docNo, 'Dated', docDate],
                ['Reference No. & Date.', referenceNo, 'Valid Until', expiryDate],
                ['Customer Name', custName, 'Status', status ? status.toUpperCase() : ''],
                ['GSTIN/UIN', custGST, 'State', custState],
                ['Prepared by', compName, '', ''],
            ];
            fieldRows.forEach(([lLabel, lVal, rLabel, rVal], i) => {
                const fy = y + i * fieldH;
                const cellW = fieldMidX - rightX - 4;
                if (i > 0) hLine(rightX, fy, rightX + rightW);
                vLine(fieldMidX, fy, fy + fieldH);
                doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
                doc.text(lLabel, rightX + 2, fy + 3.5);
                doc.setFontSize(8); doc.setFont('helvetica', 'bold'); setBlack();
                if (lVal) {
                    const lLines = doc.splitTextToSize(String(lVal), cellW);
                    doc.text(lLines[0], rightX + 2, fy + 7.5);
                }
                doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
                doc.text(rLabel, fieldMidX + 2, fy + 3.5);
                doc.setFontSize(8); doc.setFont('helvetica', 'bold'); setBlack();
                if (rVal) {
                    const rLines = doc.splitTextToSize(String(rVal), cellW);
                    doc.text(rLines[0], fieldMidX + 2, fy + 7.5);
                }
            });
            y += headerH;

            // ── Terms of Delivery / Remarks ──────────────────────────────────
            const termsH = notes ? 14 : 7;
            drawBox(M, y, CW, termsH);
            doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
            doc.text('Terms of Delivery', leftX + 2, y + 4.5);
            if (notes) {
                doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
                doc.text('Remarks:', leftX + 2, y + 9);
                doc.setFontSize(8); doc.setFont('helvetica', 'normal'); setBlack();
                doc.text(doc.splitTextToSize(notes, CW - 30), leftX + 20, y + 9);
            }
            y += termsH;

            // ── SECTION 2: CONSIGNEE + BUYER ────────────────────────────────
            const buildPartyLines = (name, address, gst, state, stateCode) => {
                const lines = [];
                lines.push({ text: name, bold: true, size: 9 });
                if (address) {
                    const wrapped = doc.setFontSize(7.5) && doc.splitTextToSize(address, leftW - 5);
                    (Array.isArray(wrapped) ? wrapped : [wrapped]).forEach(l => lines.push({ text: l, bold: false, size: 7.5 }));
                }
                if (gst) lines.push({ text: `GSTIN/UIN   : ${gst}`, bold: false, size: 7.5 });
                if (state || stateCode) {
                    const stLine = `State Name   : ${state}${stateCode ? ', Code : ' + stateCode : ''}`;
                    lines.push({ text: stLine, bold: false, size: 7.5 });
                }
                return lines;
            };
            const consigneeLines = buildPartyLines(custName, custAddress, custGST, custState, custStateCode);
            const buyerLines = buildPartyLines(custName, custAddress, custGST, custState, custStateCode);
            const partyLineH = 4.2;
            const partyContentH = Math.max(consigneeLines.length, buyerLines.length) * partyLineH + 10;
            const partyH = Math.max(partyContentH, 28);

            drawBox(M, y, CW, partyH);
            vLine(rightX, y, y + partyH);
            doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
            doc.text('Consignee (Ship to)', leftX + 2, y + 4);
            let pyL = y + 9;
            consigneeLines.forEach(l => {
                doc.setFontSize(l.size); doc.setFont('helvetica', l.bold ? 'bold' : 'normal'); setBlack();
                doc.text(l.text, leftX + 2, pyL); pyL += l.bold ? 5 : partyLineH;
            });
            doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
            doc.text('Buyer (Bill to)', rightX + 2, y + 4);
            let pyR = y + 9;
            buyerLines.forEach(l => {
                doc.setFontSize(l.size); doc.setFont('helvetica', l.bold ? 'bold' : 'normal'); setBlack();
                doc.text(l.text, rightX + 2, pyR); pyR += l.bold ? 5 : partyLineH;
            });
            y += partyH;

            // ── SECTION 3: ITEMS TABLE ───────────────────────────────────────
            const orderedItems = items.length > 0 ? [...items] : [];
            const tableBody = orderedItems.map((item, idx) => {
                const qty = parseFloat(item.quantity || 1);
                const rate = parseFloat(item.unit_price || 0);
                const amt = parseFloat(item.total_cost || (qty * rate));
                const hsnSac = item.hsn_code || item.hsn || '';
                return [
                    (idx + 1).toString(),
                    item.product_name || 'Product',
                    hsnSac,
                    `${qty.toFixed(3)} nos`,
                    rate.toFixed(3),
                    'nos',
                    fmt3(amt)
                ];
            });
            const totalQty = orderedItems.reduce((s, i) => s + parseFloat(i.quantity || 0), 0);

            if (docType === 'proforma') {
                tableBody.push(['', 'CGST', '', '', '', '', fmt3(cgst)]);
                tableBody.push(['', 'SGST', '', '', '', '', fmt3(sgst)]);
            }
            // Quotation: no tax/CGST/SGST rows — plain billing without GST breakdown
            if (discount > 0) tableBody.push(['', 'Discount', '', '', '', '', `(-) ${fmt3(discount)}`]);
            if (roundOff !== 0) tableBody.push(['', 'Less :   ROUND OFF', '', '', '', '', `(-)${Math.abs(roundOff).toFixed(3)}`]);

            autoTable(doc, {
                startY: y,
                head: [['Sl\nNo.', 'Description of Goods', 'HSN/SAC', 'Quantity', 'Rate', 'per', 'Amount']],
                body: tableBody,
                foot: [['', 'Total', '', `${totalQty.toFixed(3)} nos`, '', '', `Rs.${fmt2(grandTotal)}`]],
                theme: 'grid',
                headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 8, halign: 'center', lineColor: [0, 0, 0], lineWidth: 0.3, cellPadding: 2 },
                bodyStyles: { fontSize: 8, textColor: [0, 0, 0], lineColor: [0, 0, 0], lineWidth: 0.3, cellPadding: 2 },
                footStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 9, lineColor: [0, 0, 0], lineWidth: 0.3, cellPadding: 2 },
                columnStyles: {
                    0: { cellWidth: 10, halign: 'center' },
                    1: { cellWidth: 'auto', halign: 'left' },
                    2: { cellWidth: 22, halign: 'center' },
                    3: { cellWidth: 26, halign: 'center' },
                    4: { cellWidth: 22, halign: 'right' },
                    5: { cellWidth: 13, halign: 'center' },
                    6: { cellWidth: 28, halign: 'right' },
                },
                margin: { left: M, right: M }, tableWidth: 'auto',
            });
            y = doc.lastAutoTable.finalY;

            // ── SECTION 4: AMOUNT IN WORDS ───────────────────────────────────
            const amtWordsH = 11;
            drawBox(M, y, CW, amtWordsH);
            doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
            doc.text('Amount Chargeable (in words)', leftX + 2, y + 4);
            doc.text('E. & O.E', M + CW - 2, y + 4, { align: 'right' });
            doc.setFontSize(9); doc.setFont('helvetica', 'bold'); setBlack();
            doc.text(toWords(grandTotal), leftX + 2, y + 9);
            y += amtWordsH;

            // ── SECTION 5: HSN/SAC TAX BREAKUP (proforma only) ──────────────
            if (docType === 'proforma') {
                const hsnMap = {};
                orderedItems.forEach(item => {
                    const hsn = item.hsn_code || item.hsn || 'N/A';
                    const itemTax = parseFloat(item.tax_amount || 0);
                    const taxable = parseFloat(item.total_cost || 0) - itemTax;
                    const itemCgst = parseFloat(item.cgst_amount || itemTax / 2);
                    const itemSgst = parseFloat(item.sgst_amount || itemTax / 2);
                    const rate = parseFloat(item.tax_percent || 18);
                    if (!hsnMap[hsn]) hsnMap[hsn] = { taxable: 0, cgstRate: rate / 2, sgstRate: rate / 2, cgst: 0, sgst: 0 };
                    hsnMap[hsn].taxable += taxable;
                    hsnMap[hsn].cgst += itemCgst;
                    hsnMap[hsn].sgst += itemSgst;
                });
                const hsnEntries = Object.entries(hsnMap);
                const hsnRows = hsnEntries.length > 0
                    ? hsnEntries.map(([hsn, v]) => [hsn, fmt3(v.taxable), `${v.cgstRate}%`, fmt3(v.cgst), `${v.sgstRate}%`, fmt3(v.sgst), fmt3(v.cgst + v.sgst)])
                    : [['N/A', fmt3(subtotal), `${cgstRate}%`, fmt3(cgst), `${sgstRate}%`, fmt3(sgst), fmt3(cgst + sgst)]];
                const hsnTotCgst = hsnEntries.reduce((s, [, v]) => s + v.cgst, 0) || cgst;
                const hsnTotSgst = hsnEntries.reduce((s, [, v]) => s + v.sgst, 0) || sgst;
                const hsnTotTaxable = hsnEntries.reduce((s, [, v]) => s + v.taxable, 0) || subtotal;
                autoTable(doc, {
                    startY: y,
                    head: [['HSN/SAC', 'Taxable\nValue', { content: 'CGST', colSpan: 2 }, { content: 'SGST/UTGST', colSpan: 2 }, 'Total\nTax Amount'],
                    ['', '', 'Rate', 'Amount', 'Rate', 'Amount', '']],
                    body: [...hsnRows, ['Total', fmt3(hsnTotTaxable), '', fmt3(hsnTotCgst), '', fmt3(hsnTotSgst), fmt3(hsnTotCgst + hsnTotSgst)]],
                    theme: 'grid',
                    headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 7.5, halign: 'center', lineColor: [0, 0, 0], lineWidth: 0.3, cellPadding: 1.5 },
                    bodyStyles: { fontSize: 8, textColor: [0, 0, 0], lineColor: [0, 0, 0], lineWidth: 0.3, cellPadding: 2, halign: 'center' },
                    footStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold', fontSize: 8, lineColor: [0, 0, 0], lineWidth: 0.3, cellPadding: 2, halign: 'center' },
                    margin: { left: M, right: M }, tableWidth: 'auto',
                });
                y = doc.lastAutoTable.finalY;

                const taxWordsH = 8;
                drawBox(M, y, CW, taxWordsH);
                doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
                doc.text('Tax Amount (in words) :', leftX + 2, y + 5);
                doc.setFontSize(8); doc.setFont('helvetica', 'bold'); setBlack();
                doc.text(toWords(hsnTotCgst + hsnTotSgst), leftX + 42, y + 5);
                y += taxWordsH;
            }

            // ── TERMS & CONDITIONS ────────────────────────────────────────────
            if (termsConditions) {
                const tcTextLines = doc.splitTextToSize(termsConditions, CW - 6);
                const tcH = Math.max(tcTextLines.length * 4 + 8, 16);
                drawBox(M, y, CW, tcH);
                doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); setBlack();
                doc.text('Terms & Conditions :', leftX + 2, y + 5);
                doc.setFontSize(7.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(50, 50, 50);
                doc.text(tcTextLines, leftX + 2, y + 10);
                y += tcH;
            }

            // ── SECTION 6: BOTTOM — DECLARATION | BANK | SIGNATURES ──────────
            const bottomH = 35;
            const bLeftW = Math.round(CW * 0.36);
            const bMidW = Math.round(CW * 0.34);
            const bMidX = M + bLeftW;
            const bRightX = bMidX + bMidW;

            drawBox(M, y, CW, bottomH);
            vLine(bMidX, y, y + bottomH);
            vLine(bRightX, y, y + bottomH);

            doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); setBlack();
            doc.text('Declaration', leftX + 2, y + 5);
            doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.setTextColor(50, 50, 50);
            const declText = docType === 'proforma'
                ? 'We declare that this proforma invoice shows the actual\nprice of the goods described and all particulars are\ntrue and correct.'
                : 'This is a quotation and not a final invoice.\nPrices are subject to change. Kindly confirm the\norder before delivery.';
            doc.text(doc.splitTextToSize(declText, bLeftW - 4), leftX + 2, y + 10);

            doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); setBlack();
            doc.text("Company's Bank Details", bMidX + 2, y + 5);
            doc.setFontSize(7.5); doc.setFont('helvetica', 'normal');
            if (defaultBank) {
                const labelX = bMidX + 2;
                const valueX = bMidX + 28;
                let bdy = y + 11;
                [
                    ['Bank Name', defaultBank.bank_name || 'N/A'],
                    ['A/c No.', defaultBank.account_number || 'N/A'],
                    ['Branch & IFS Code', `${defaultBank.branch_name ? defaultBank.branch_name + ' & ' : ''}${defaultBank.ifsc_code || 'N/A'}`],
                ].forEach(([label, val]) => {
                    doc.setFont('helvetica', 'normal'); doc.setTextColor(60, 60, 60);
                    doc.text(label, labelX, bdy);
                    doc.setFont('helvetica', 'bold'); setBlack();
                    const vLines = doc.splitTextToSize(`: ${val}`, bMidW - 30);
                    doc.text(vLines, valueX, bdy);
                    bdy += vLines.length > 1 ? vLines.length * 3.8 + 1 : 5;
                });
            } else {
                doc.setFont('helvetica', 'normal'); doc.setTextColor(120, 120, 120);
                doc.text('No bank account configured', bMidX + 2, y + 15);
            }

            doc.setFontSize(7.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(50, 50, 50);
            doc.text("Customer's Seal and Signature", bRightX + 2, y + 5);
            doc.setFontSize(8); doc.setFont('helvetica', 'bold'); setBlack();
            doc.text(`for ${compName}`, bRightX + 2, y + bottomH - 9);
            doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(50, 50, 50);
            doc.text('Authorised Signatory', bRightX + 2, y + bottomH - 4);
            y += bottomH;

            // ── SECTION 7: FOOTER ────────────────────────────────────────────
            const footerH = 7;
            drawBox(M, y, CW, footerH);
            doc.setFontSize(7.5); doc.setFont('helvetica', 'italic'); doc.setTextColor(50, 50, 50);
            doc.text(`This is a Computer Generated ${docTitle}`, PW / 2, y + 4.5, { align: 'center' });
            y += footerH;

            // ── OUTER BORDER ─────────────────────────────────────────────────
            doc.setDrawColor(...BK); doc.setLineWidth(0.6);
            doc.rect(M, M + 10, CW, y - M - 10, 'S');

            // ── SAVE ─────────────────────────────────────────────────────────
            MySwal.close();
            const copyLabelForFile = { original: 'Original', duplicate: 'Duplicate', extra: 'ExtraCopy' }[copyType] || 'Original';
            doc.save(`${docType === 'proforma' ? 'Proforma' : 'Quotation'}_${docNo.replace(/[^a-zA-Z0-9]/g, '_')}_${copyLabelForFile}.pdf`);
            MySwal.fire({ icon: 'success', title: 'PDF Downloaded', timer: 1500, showConfirmButton: false });
        } catch (error) {
            MySwal.close();
            MySwal.fire({ icon: 'error', title: 'PDF Error', text: error.message });
        } finally { setLoading(false); }
    };


    // Table helpers
    const handleSelectAll = (e) => { if (e.target.checked) setSelectedRows(quotations.map(q => q.id)); else setSelectedRows([]); };
    const handleSelectRow = (id, checked) => { if (checked) setSelectedRows([...selectedRows, id]); else setSelectedRows(selectedRows.filter(rowId => rowId !== id)); };
    const getStatusBadge = (status) => {
        const badges = {
            'draft': { class: 'badge bg-secondary', icon: <FileText size={12} /> },
            'pending': { class: 'badge bg-warning', icon: <Clock size={12} /> },
            'approved': { class: 'badge bg-success', icon: <CheckCircle size={12} /> },
            'rejected': { class: 'badge bg-danger', icon: <XCircle size={12} /> },
            'expired': { class: 'badge bg-dark', icon: <AlertTriangle size={12} /> },
            'converted': { class: 'badge bg-info', icon: <RefreshCw size={12} /> }
        };
        const b = badges[status] || { class: 'badge bg-secondary', icon: null };
        return <span className={b.class}>{b.icon} {status}</span>;
    };
    const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount || 0);
    const formatDate = (date) => date ? dayjs(date).format('DD/MM/YYYY') : 'N/A';
    const isExpired = (expiryDate) => expiryDate && dayjs(expiryDate).isBefore(dayjs());

    const stats = {
        totalQuotations: pagination.total,
        totalAmount: quotations.reduce((s, q) => s + (q.grand_total || 0), 0),
        pendingCount: quotations.filter(q => q.status === 'pending').length,
        approvedCount: quotations.filter(q => q.status === 'approved').length,
        convertedCount: quotations.filter(q => q.status === 'converted').length
    };

    const renderTooltip = (props, text) => <Tooltip id={`tt-${text}`} {...props}>{text}</Tooltip>;

    const columns = [
        { title: <input type="checkbox" className="form-check-input" checked={selectedRows.length === quotations.length && quotations.length > 0} onChange={handleSelectAll} />, dataIndex: "checkbox", key: "checkbox", width: "40px", render: (text, record) => <input type="checkbox" className="form-check-input" checked={selectedRows.includes(record.id)} onChange={(e) => handleSelectRow(record.id, e.target.checked)} /> },
        { title: activeTab === 'proforma' ? 'Proforma No' : 'Quotation No', dataIndex: "quotation_no", sorter: true, render: (text, record) => <div><strong>{text}</strong><br /><small className="text-muted">Created: {formatDate(record.created_at)}</small></div> },
        { title: "Date", dataIndex: "quotation_date", sorter: true, render: (text, record) => <div><div>{formatDate(text)}</div>{record.expiry_date && <small className={isExpired(record.expiry_date) ? 'text-danger' : 'text-muted'}>Expires: {formatDate(record.expiry_date)}</small>}</div> },
        { title: "Customer", dataIndex: "customer_name", sorter: true, render: (text, record) => <div className="d-flex align-items-center"><div className="avatar avatar-sm bg-primary rounded-circle d-flex align-items-center justify-content-center text-white me-2">{text?.charAt(0).toUpperCase()}</div><div><div>{text}</div><small className="text-muted">{record.customer_phone || ''}</small></div></div> },
        { title: "Amount", dataIndex: "grand_total", sorter: true, render: (text, record) => <div><strong className="text-primary">{formatCurrency(text)}</strong><br /><small className="text-muted">Items: {record.items_count || 0}</small></div> },
        { title: "Status", dataIndex: "status", sorter: true, render: (text) => getStatusBadge(text) },
        { title: "Valid Till", dataIndex: "expiry_date", render: (text) => <span className={isExpired(text) ? 'text-danger' : ''}>{formatDate(text)}{isExpired(text) && <small className="d-block text-danger">Expired</small>}</span> },
        {
            title: "Actions", dataIndex: "actions", render: (text, record) => (
                <div className="d-flex align-items-center gap-1" style={{ position: 'relative' }}>
                    <button className="btn btn-sm btn-outline-info" onClick={() => handleView(record)}><Eye size={14} /></button>
                    <button className="btn btn-sm btn-outline-primary" onClick={() => handleEdit(record)} disabled={!canEdit || record.status === 'converted' || record.status === 'expired'}><Edit size={14} /></button>
                    <button
                        className="btn btn-sm btn-outline-warning"
                        onClick={() => downloadQuotationPDF(record, 'original')}
                        disabled={loading}
                        title="Download PDF"
                    ><File size={14} /></button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(record)} disabled={!canDelete || record.status === 'converted'}><Trash2 size={14} /></button>
                </div>
            )
        }
    ];

    return (
        <div className="page-wrapper">
            <div className="content">
                <div className="page-header">
                    <div className="add-item d-flex">
                        <div className="page-title">
                            <h4>{activeTab === 'proforma' ? 'Proforma Invoices' : 'Quotations'}</h4>
                            <h6>{activeTab === 'proforma' ? 'Manage proforma invoices' : 'Manage your sales quotations'}</h6>
                        </div>
                    </div>
                    <ul className="table-top-head">
                        {selectedRows.length > 0 && <li><button className="btn btn-danger btn-sm"><Trash2 size={16} className="me-1" /> Delete ({selectedRows.length})</button></li>}
                        <li>{canCreate && (
                            <button className="btn btn-primary" onClick={() => navigate(`/create-quotation?type=${activeTab}`)}>
                                <PlusCircle size={18} className="me-2" />
                                {activeTab === 'proforma' ? 'New Proforma' : 'New Quotation'}
                            </button>
                        )}</li>

                        <li><OverlayTrigger placement="top" overlay={p => renderTooltip(p, 'Refresh')}><button className="btn btn-outline-secondary" onClick={() => loadQuotations()}><RotateCcw size={18} /></button></OverlayTrigger></li>
                        <li><OverlayTrigger placement="top" overlay={p => renderTooltip(p, 'Collapse')}><button className={data ? "btn btn-outline-secondary active" : "btn btn-outline-secondary"} onClick={() => dispatch(setToogleHeader(!data))}><ChevronUp size={18} /></button></OverlayTrigger></li>
                    </ul>
                </div>

                {/* Tab Control */}
                <div className="mb-3">
                    <ul className="nav nav-tabs">
                        <li className="nav-item">
                            <button
                                className={`nav-link${activeTab === 'quotation' ? ' active fw-bold' : ''}`}
                                onClick={() => { setActiveTab('quotation'); setPagination(prev => ({ ...prev, page: 1 })); }}
                            >
                                <FileText size={16} className="me-2" />Quotation
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link${activeTab === 'proforma' ? ' active fw-bold' : ''}`}
                                onClick={() => { setActiveTab('proforma'); setPagination(prev => ({ ...prev, page: 1 })); }}
                            >
                                <File size={16} className="me-2" />Proforma Invoice
                            </button>
                        </li>
                    </ul>
                </div>

                <div className="row mb-4">
                    <div className="col-xl-3 col-sm-6 col-12"><div className="card"><div className="card-body"><h5 className="card-title text-muted">Total {activeTab === 'proforma' ? 'Proformas' : 'Quotations'}</h5><h3 className="mb-0 text-primary">{stats.totalQuotations}</h3><small>All time</small></div></div></div>
                    <div className="col-xl-3 col-sm-6 col-12"><div className="card"><div className="card-body"><h5 className="card-title text-muted">Total Amount</h5><h3 className="mb-0 text-primary">{formatCurrency(stats.totalAmount)}</h3></div></div></div>
                    <div className="col-xl-3 col-sm-6 col-12"><div className="card"><div className="card-body"><h5 className="card-title text-muted">Pending</h5><h3 className="mb-0 text-warning">{stats.pendingCount}</h3></div></div></div>
                    <div className="col-xl-3 col-sm-6 col-12"><div className="card"><div className="card-body"><h5 className="card-title text-muted">Approved</h5><h3 className="mb-0 text-success">{stats.approvedCount}</h3></div></div></div>
                </div>

                <div className="card table-list-card">
                    <div className="card-body">
                        <div className="table-top">
                            <div className="search-set"><div className="search-input"><input type="text" placeholder="Search by quotation no, customer..." className="form-control form-control-sm" value={filters.search} onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))} onKeyPress={(e) => e.key === 'Enter' && handleSearch()} /><button className="btn btn-searchset" onClick={handleSearch}><Search size={16} /></button></div></div>
                            <div className="search-path"><div className="d-flex align-items-center"><button className={`btn btn-filter ${isFilterVisible ? "setclose" : ""}`} onClick={toggleFilterVisibility}><Filter size={18} /></button></div></div>
                            <div className="form-sort"><Sliders size={16} className="info-img me-2" /><Select className="select" options={sortOptions} value={sortOptions.find(opt => opt.value === sortOption)} onChange={handleSortChange} isClearable={false} /></div>
                        </div>

                        <div className={`card${isFilterVisible ? ' visible' : ''}`} id="filter_inputs" style={{ display: isFilterVisible ? 'block' : 'none' }}>
                            <div className="card-body pb-0">
                                <div className="row">
                                    <div className="col-lg-3 col-sm-6 col-12"><div className="input-blocks"><User size={16} className="info-img" /><Select options={customers} value={customers.find(c => c.value === filters.customer_id)} onChange={(opt) => handleFilterChange('customer_id', opt?.value || 0)} placeholder="All Customers" isClearable /></div></div>
                                    <div className="col-lg-3 col-sm-6 col-12"><div className="input-blocks"><StopCircle size={16} className="info-img" /><Select options={statusFilterOptions} value={statusFilterOptions.find(s => s.value === filters.status)} onChange={(opt) => handleFilterChange('status', opt?.value || '')} placeholder="All Status" isClearable /></div></div>
                                    <div className="col-lg-6 col-sm-12 col-12"><div className="input-blocks"><div className="position-relative daterange-wraper"><Calendar size={16} className="info-img" /><RangePicker value={filters.startDate && filters.endDate ? [filters.startDate, filters.endDate] : null} onChange={handleDateRangeChange} format="DD/MM/YYYY" placeholder={['From Date', 'To Date']} style={{ width: '100%' }} ranges={datePresets} /></div></div></div>
                                    <div className="col-lg-3 col-sm-6 col-12"><div className="input-blocks"><button className="btn btn-primary w-100" onClick={() => loadQuotations(1)}><Search size={16} className="me-2" />Apply Filters</button></div></div>
                                    <div className="col-lg-3 col-sm-6 col-12"><div className="input-blocks"><button className="btn btn-secondary w-100" onClick={resetFilters}><RotateCcw size={16} className="me-2" />Reset</button></div></div>
                                </div>
                            </div>
                        </div>

                        <div className="table-responsive">
                            <Table columns={columns} dataSource={quotations} loading={loading} pagination={{ current: pagination.page, pageSize: pagination.limit, total: pagination.total, onChange: handlePageChange, showSizeChanger: true, showQuickJumper: true, showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} quotations` }} />
                        </div>
                    </div>
                </div>
            </div>

            {modalType === 'view' && selectedQuotation && <ViewQuotation quotation={selectedQuotation} onClose={handleModalClose} />}
            {modalType === 'edit' && selectedQuotation && <EditQuotation quotationId={selectedQuotation.id} onSuccess={handleSuccess} onClose={handleModalClose} isOpen={true} />}
        </div>
    );
};

export default QuotationList;
